import 'highlight.js/styles/default.css';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cloudinary from 'cloudinary-core';
import CloudinaryUtil from '../../core/CloudinaryUtil';
import Highlight from 'react-highlight/lib/optimized';

export default class CodeSampleContent extends Component {
  static propTypes = {
    publicId: PropTypes.string,
    transformation: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.arrayOf(PropTypes.object)
    ]),
    cloudName: PropTypes.string,
    displayDpr: PropTypes.bool
  };

  constructor(props, context) {
    super(props, context);
    this.cl = props.cl;
    if (!this.cl) {
      this.cl = cloudinary.Cloudinary.new();
      this.cl.fromDocument();
    }
  }

  getTransformationForPresentation() {
    let result = CloudinaryUtil.changeAspectRatioToDimenssions(this.props.transformation);
    if (!this.props.displayDpr) {
      result = CloudinaryUtil.removeDpr(result);
    }
    return CloudinaryUtil.ensureNonEmptyTransformation(result);
  }

  node_code(transformationToDisplay) {
    return 'cloudinary.image("' + this.props.publicId + '", ' + this.javascript_transformation(transformationToDisplay, this.props.format) + ');';
  }

  jquery_code(transformationToDisplay) {
    return '$.cloudinary.image("' + this.props.publicId + '", ' + this.javascript_transformation(transformationToDisplay, this.props.format) + ');';
  }

  javascript_transformation(t, format) {
    if (Array.isArray(t)) {
      if (format) {
        format = `, \"format\": "${format}"`;
      } else {
        format = '';
      }
      return '{"transformation":\n\t' + JSON.stringify(t).replace(/\},\{/g, '},\n\t\t{') + `${format}}`;
    } else {
      return JSON.stringify({...t, format: format});
  }
}

ruby_code(transformationToDisplay) {
  return 'cl_image_tag("' + this.props.publicId + '", ' + this.ruby_hash(transformationToDisplay, this.props.format) + ')';
}

ruby_hash(h, format) {
  if (format) {
    format = `, :format => "${format}"`;
  } else {
    format = '';
  }
  if (Array.isArray(h)) {
    return ':transformation => [\n\t{' + h.map((t) => this.ruby_hash(t, null)).join('},\n\t{') + `}]${format}`;
  } else {
    return Object.keys(h).map(k => `:${k} => ${JSON.stringify(h[k])}`).join(', ') + format;
  }
}

php_code(transformationToDisplay) {
  let isArray = Array.isArray(transformationToDisplay);
  return 'cl_image_tag("' + this.props.publicId + '", ' +
    this.php_hash(transformationToDisplay, this.props.format) + ');';
}

php_hash(h, format) {
  if (format) {
    format = `, "format"=>"${format}"`;
  } else {
    format = '';
  }
  if (Array.isArray(h)) {
    return 'array("transformation" => array(\n\t' + h.map(t => this.php_hash(t, null)).join(',\n\t') + `)${format})`;
  } else {
    return 'array(' + Object.keys(h).map(k => `"${k}"=>${JSON.stringify(h[k])}`).join(', ') + `${format})`;
  }
}

python_code(transformationToDisplay) {
  return 'CloudinaryImage("' + this.props.publicId + '").image(transformation=' +
    JSON.stringify(transformationToDisplay).replace(/":("|\d)/g, '": $1') +
    (this.props.format ? `, format="${this.props.format}"` : '') +
    ')';
}

url_code(transformationToDisplay) {
  let href = this.cl.url(this.props.publicId, { transformation: transformationToDisplay, format: this.props.format });

  //the transformation to be presented in the URL code
  let codeTransformation = new cloudinary.Transformation();
  codeTransformation.fromOptions(transformationToDisplay);
  let transformation = codeTransformation.toString();
  let transformationSeperator = '';
  // Don't add a redundant separator if the transformation is empty (showing original image)
  if (transformation && transformation.length > 0) {
    transformationSeperator = '/';
  }
  return (
    <code>
      <a href={href} target="_blank">
        <span className="prefix">{'https://res.cloudinary.com/' + this.props.cloudName + '/'}</span>
        <span className="kind">{'image/upload/'}</span>
        <span className="trans">{transformation}{transformationSeperator}</span>
        <span className="public_id">{this.props.publicId}</span>
        <span className="format">{this.props.format ? '.' + this.props.format : ''}</span>
      </a>
    </code>);
}

android_code(transformationToDisplay) {
  return this.java_code(transformationToDisplay);
}
java_code(transformationToDisplay) {
  let format = this.props.format ? `.format("${this.props.format}")` : '';
  return 'cloudinary.url().transformation(\n\tnew Transformation().' + this.java_hash(transformationToDisplay) + '\n)' + format + '.imageTag("' + this.props.publicId + '");';
}

java_hash(h) {
  if (Array.isArray(h)) {
    return h.map(t => this.java_hash(t)).join('.chain().\n\t\t');
  } else {
    return Object.keys(h).map(k => `${this.camelize(k)}(${JSON.stringify(h[k])})`).join('.');
  }
}

csharp_code(transformationToDisplay) {
  let format = this.props.format ? `.Format("${this.props.format}")` : '';
  return 'cloudinary.Api.UrlImgUp.Transform(\n\tnew Transformation().' + this.csharp_hash(transformationToDisplay) + '\n)' + format + '.BuildImageTag("' + this.props.publicId + '");';
}

csharp_hash(h) {
  if (Array.isArray(h)) {
    return h.map((t) => this.csharp_hash(t)).join('.Chain().\n\t\t');
  } else {
    return Object.keys(h).map(k => `${this.Camelize(k)}(${JSON.stringify(h[k])})`).join('.');
  }
}

scala_code(transformationToDisplay) {
  let format = this.props.format ? `.format("${this.props.format}")` : '';
  return 'cloudinary.url().transformation(Transformation().\n\t' + this.scala_hash(transformationToDisplay) + '\n)' + format + '.imageTag("' + this.props.publicId + '")';
}

scala_hash(h) {
  if (Array.isArray(h)) {
    return h.map(t => this.scala_hash(t)).join('./.\n\t\t');
  } else {
    return Object.keys(h).map(k => `${this.camelize(k)}(${JSON.stringify(h[k])})`).join('.');
  }
}

angular_code(transformationToDisplay) {
  let format = this.props.format ? ` format="${this.props.format}"` : '';
  return '<cl-image public-id="' + this.props.publicId +
    '"' + format + ' ' + this.angular_hash(transformationToDisplay) +
    (Array.isArray(transformationToDisplay) ? '</cl-image>' : ' />');
}

angular_hash(h) {
  if (Array.isArray(h)) {
    return '>\n\t' + h.map(t => '<cl-transformation ' + this.angular_hash(t) + ' />').join('\n\t') + '\n';
  } else {
    return Object.keys(h).map(k => `${this.dasherize(k)}="${h[k]}"`).join(' ');
  }
}

camelize(str) {
  return str.replace(/(?:^\w|[A-Z]|\b\w)/g, function (letter, index) {
    return index == 0 ? letter.toLowerCase() : letter.toUpperCase();
  }).replace(/\s+/g, '');
}

Camelize(str) {
  return str.replace(/(?:^\w|[A-Z]|\b\w)/g, function (letter, index) {
    return letter.toUpperCase();
  }).replace(/\s+/g, '');
}

dasherize(str) {
  return str.replace(/(?:^\w|[A-Z]|\b\w)/g, function (letter, index) {
    return index == 0 ? letter.toLowerCase() : ('-' + letter.toLowerCase());
  }).replace(/\s+/g, '');
}


render() {
  var transformationToDisplay = this.getTransformationForPresentation();

  let code = this[this.props.name + '_code'](transformationToDisplay);
  if (this.props.name != 'url') {
    let className = this.props.language;
    code = <Highlight className={className} >{code}</Highlight>;
  }
  let className = this.props.name;
  return (
    <div className={this.props.selected ? 'active' : ''}>
      {code}
    </div>
  );
}
}



/** WEBPACK FOOTER **
 ** ./src/js/components/CodeSample/CodeSampleContent.js
 **/